import { useEffect, useRef, useState } from "react";
import {
  bestellgruppenVergleichLaden,
  type Bestellgruppe,
  type BestellgruppenVergleich,
} from "../../api/bestellgruppen.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { Order, SendungsTreffer } from "../../lib/types.ts";
import { SendungsSuche } from "../Shipments/SendungsSuche.tsx";

export interface DsvGruppenVergleichProps {
  gruppe: Bestellgruppe;
  bestellungen: readonly Order[];
  sendungsSuche?: (begriff: string) => Promise<SendungsTreffer[]>;
  onBestaetigen: (shipmentId: string) => void | Promise<void>;
}

const KEINE_SENDUNGSSUCHE = (): Promise<SendungsTreffer[]> => Promise.resolve([]);

function kennung(bestellungen: readonly Order[], orderId: string): string {
  const bestellung = bestellungen.find((eintrag) => String(eintrag.id) === orderId);
  return String(bestellung?.orderNo || bestellung?.name || orderId);
}

function OrderListe({ titel, orderIds, bestellungen }: {
  titel: string;
  orderIds: readonly string[];
  bestellungen: readonly Order[];
}) {
  return (
    <section>
      <h4>{titel}</h4>
      {orderIds.length ? <ul>{orderIds.map((orderId) => <li key={orderId}>{kennung(bestellungen, orderId)}</li>)}</ul> : <p>Keine Bestellungen.</p>}
    </section>
  );
}

export function DsvGruppenVergleich({
  gruppe,
  bestellungen,
  sendungsSuche,
  onBestaetigen,
}: DsvGruppenVergleichProps) {
  const [sendung, setSendung] = useState<SendungsTreffer | null>(null);
  const [vergleich, setVergleich] = useState<BestellgruppenVergleich | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bestaetigungLaeuft, setBestaetigungLaeuft] = useState(false);
  const bestaetigungLaeuftRef = useRef(false);

  async function bestaetigen(): Promise<void> {
    if (!sendung || bestaetigungLaeuftRef.current) return;
    bestaetigungLaeuftRef.current = true;
    setBestaetigungLaeuft(true);
    try {
      await onBestaetigen(sendung.id);
    } finally {
      bestaetigungLaeuftRef.current = false;
      setBestaetigungLaeuft(false);
    }
  }

  useEffect(() => {
    if (!sendung) return;
    let abgebrochen = false;
    setVergleich(null);
    setFehler(null);
    bestellgruppenVergleichLaden(gruppe.id, sendung.id)
      .then((antwort) => { if (!abgebrochen) setVergleich(antwort); })
      .catch((ursache: unknown) => {
        // Die Zeile unter dem Suchfeld gab es schon (Spec R2): neu ist der Text. Der feste Satz wird zum Kontext.
        const kontext = "Der Vergleich konnte nicht geladen werden.";
        if (!abgebrochen) setFehler(fehlertext(ursache, kontext) ?? kontext);
      });
    return () => { abgebrochen = true; };
  }, [gruppe.id, sendung]);

  return (
    <div className="field-grid">
      <div className="field wide">
        <p>Die DSV-Sendung wird nur an der Bestellgruppe gespeichert; einzelne Bestellungen bleiben unverändert.</p>
        <SendungsSuche
          suche={sendungsSuche ?? KEINE_SENDUNGSSUCHE}
          onAuswahl={setSendung}
          disabled={bestaetigungLaeuft}
        />
      </div>
      {sendung && vergleich ? (
        <section role="dialog" aria-label="DSV-Sendung mit Bestellgruppe vergleichen" className="dsv-gruppen-vergleich">
          <h3>DSV-Sendung mit Bestellgruppe vergleichen</h3>
          <OrderListe titel="Bestellungen der Gruppe" orderIds={vergleich.groupOrderIds} bestellungen={bestellungen} />
          <OrderListe titel="Bestellungen der DSV-Sendung" orderIds={vergleich.shipmentOrderIds} bestellungen={bestellungen} />
          <OrderListe titel="Nur in der Bestellgruppe" orderIds={vergleich.onlyInGroup} bestellungen={bestellungen} />
          <OrderListe titel="Nur in der DSV-Sendung" orderIds={vergleich.onlyInShipment} bestellungen={bestellungen} />
          <button type="button" onClick={() => setSendung(null)}>Abbrechen</button>
          <button type="button" disabled={bestaetigungLaeuft} onClick={() => void bestaetigen()}>
            Verknüpfung der Bestellgruppe bestätigen
          </button>
        </section>
      ) : null}
      {sendung && !vergleich && !fehler ? <p role="status">Vergleich wird geladen …</p> : null}
      {fehler ? <p role="alert">{fehler}</p> : null}
    </div>
  );
}
